import { Ambulance, ArrowLeft, Frown, Gift, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';

import {
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  buttonVariants,
} from '@cold-ambulance/shadcn';
import { getIncompleteProfileTab } from '@cold-ambulance/isomorphic';
import {
  AwaitingSignatureBanner,
  IncompleteProfileBanner,
  PageContainer,
  PendingDocumentsBanner,
  PendingVerificationBanner,
  useData,
} from '@cold-ambulance/ui';

import HealthcareBag from '../../assets/icons/healthcare-bag.tsx';
import { ActiveServiceCard } from '../components/active-service-card.tsx';
import {
  RequestCard,
  RequestCardSkeleton,
} from '../components/request-card.tsx';
import { useUser } from '../firebase.tsx';

export default function Home() {
  const { t } = useTranslation();
  const userQuery = useUser();
  const isApproved = userQuery.isPending
    ? true
    : (userQuery.data?.approved ?? false);
  const hasPendingDocuments = (userQuery.data?.pendingDocumentsCount ?? 0) > 0;
  const incompleteProfileTab = getIncompleteProfileTab(userQuery.data);

  const activeServicesQuery = useData('GET /me/requests', {
    pageSize: 10,
    active: 'true',
  });

  const requestsQuery = useData('GET /me/requests', {
    pageSize: 4,
  });

  const hasActiveServices =
    !activeServicesQuery.isLoading &&
    (activeServicesQuery.data?.records.length ?? 0) > 0;

  return (
    <PageContainer title={t('home.title')}>
      {!isApproved && <PendingVerificationBanner />}
      {incompleteProfileTab && (
        <IncompleteProfileBanner className="mb-6" to={`/profile?tab=${incompleteProfileTab}`} />
      )}
      {hasPendingDocuments && <PendingDocumentsBanner />}
      <ContractReadyBanner />
      <NewOfferBanner />
      {hasActiveServices && (
        <ActiveServicesSection services={activeServicesQuery.data!.records} />
      )}
      <div className="flex flex-col items-center justify-center rounded-3xl border border-[#E5E7EB] p-6">
        <div className="mb-6 flex items-center justify-center rounded-full bg-[#28A745]/10 p-6">
          <Ambulance size={48} color="#2E7D32" />
        </div>
        <div className="flex flex-col items-center justify-center gap-3">
          <h1 className="text-3xl font-bold">{t('home.needMedicalService')}</h1>
          <p className="mb-8 text-center text-[18px] text-[#4B5563]">
            {t('home.requestDescription')}{' '}
            <span className="font-bold">
              {t('home.requestDescriptionBold')}{' '}
            </span>
            {t('home.requestDescriptionEnd')}
          </p>
        </div>
        {isApproved ? (
          <Link to="/requests/new">
            <Button className="gap flex flex-row items-center justify-center gap-3 rounded-2xl px-12 py-8">
              <p className="text-[20px] font-bold">{t('home.requestMedicalService')}</p>
            </Button>
          </Link>
        ) : (
          <Button
            className="gap flex flex-row items-center justify-center gap-3 rounded-2xl px-12 py-8 opacity-50"
            onClick={() => toast.info(t('home.accountUnderReview'))}
          >
            <p className="text-[20px] font-bold">{t('home.requestMedicalService')}</p>
          </Button>
        )}
        {/* <div className="mt-8 flex flex-row gap-4">
            <div className="flex flex-row gap-2 rounded-full bg-[#F3F4F6] p-3">
              <Paramedic />
              <p className="text-sm text-[#6B7280]">اسعاف طوارئ</p>
            </div>
            <div className="flex flex-row gap-2 rounded-full bg-[#F3F4F6] p-3">
              <HealthcareBag size={16} />
              <p className="text-sm text-[#6B7280]">عيادة متنقلة</p>
            </div>
          </div> */}
      </div>
      {requestsQuery.isLoading ? (
        <RequestsSkeleton />
      ) : requestsQuery.data?.records.length ? (
        <RequestsListSection />
      ) : (
        <EmptyRequestsSection />
      )}
    </PageContainer>
  );
}

function RequestsListSection() {
  const { t } = useTranslation();
  const requestsQuery = useData('GET /me/requests', {
    pageSize: 4,
  });

  return (
    <>
      <div className="flex w-full items-center justify-between">
        <h1 className="mt-8 mb-7 text-3xl font-bold">{t('home.myRequests')}</h1>
        <Link
          to="/requests"
          className="text-primary flex gap-x-1 text-sm font-bold"
        >
          <span>{t('common.viewAll')}</span>
          <ArrowLeft />
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {requestsQuery.data?.records.map((request) => (
          <RequestCard key={request.id} request={request} />
        ))}
      </div>
    </>
  );
}

function EmptyRequestsSection() {
  const { t } = useTranslation();
  return (
    <>
      <h1 className="mt-8 mb-7 text-3xl font-bold">{t('home.myRequests')}</h1>
      <div className="flex h-80 flex-col items-center justify-center space-y-4 rounded-lg border text-center">
        <div>
          <p className="text-xl font-bold">{t('home.noRequestsTitle')}</p>
          <p className="">{t('home.noRequestsDescription')}</p>
        </div>
      </div>
    </>
  );
}

function RequestsSkeleton() {
  const { t } = useTranslation();
  return (
    <>
      <h1 className="mt-8 mb-7 text-3xl font-bold">{t('home.myRequests')}</h1>

      <div className="grid grid-cols-2 gap-4">
        <RequestCardSkeleton />
        <RequestCardSkeleton />
      </div>
    </>
  );
}

function ActiveServicesSection({
  services,
}: {
  services: Parameters<typeof ActiveServiceCard>[0]['service'][];
}) {
  if (services.length === 1) {
    return (
      <div className="mb-6">
        <ActiveServiceCard service={services[0]} />
      </div>
    );
  }

  return (
    <div className="relative mb-6 px-12">
      <Carousel
        opts={{
          align: 'start',
          direction: 'rtl',
        }}
        className="w-full"
      >
        <CarouselContent>
          {services.map((service) => (
            <CarouselItem key={service.id}>
              <ActiveServiceCard service={service} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="-start-0" />
        <CarouselNext className="-end-0" />
      </Carousel>
    </div>
  );
}


function ContractReadyBanner() {
  const { t } = useTranslation();
  const notificationsQuery = useData('GET /notifications', {
    read: false,
    page: 1,
    pageSize: 10,
  });

  const contractReadyNotifications =
    notificationsQuery.data?.records.filter(
      (it) => it.type === 'CONTRACT_READY',
    ) ?? [];

  if (contractReadyNotifications.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 space-y-4">
      {contractReadyNotifications.map((notification) => {
        const metadata = notification.metadata as {
          projectName?: string;
        } | null;
        return (
          <AwaitingSignatureBanner
            key={notification.id}
            projectName={metadata?.projectName ?? t('home.defaultProjectName')}
            linkUrl={notification.actionUrl || '/requests'}
          />
        );
      })}
    </div>
  );
}

function NewOfferBanner() {
  const { t } = useTranslation();
  const notificationsQuery = useData('GET /notifications', {
    read: false,
    page: 1,
    pageSize: 10,
  });

  const newOfferNotifications =
    notificationsQuery.data?.records.filter((it) => it.type === 'NEW_OFFER') ??
    [];

  if (newOfferNotifications.length === 0) {
    return null;
  }

  const notification = newOfferNotifications[0];

  return (
    <div className="mt-8 flex items-center justify-between rounded-xl bg-[#E8F5E9] py-3 ps-7 pe-4 mb-4">
      <div className="flex items-center gap-2">
        <Gift size={20} className="text-[#2E7D32]" />
        <p className="text-sm font-medium text-black">{notification.message}</p>
      </div>
      <Link
        to={notification.actionUrl || '/requests'}
        className={buttonVariants({
          variant: 'link',
          className: 'bg-[#2E7D32] text-sm text-white',
        })}
      >
        {t('home.viewOffer')}
      </Link>
    </div>
  );
}
